import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTANTS = [5000, 10000, 20000, 50000];
const MONTANT_MIN = 2000;
const METHODES = [
  { code: "WAVE", nom: "Wave" },
  { code: "ORANGE", nom: "Orange Money" },
  { code: "MTN", nom: "MTN MoMo" },
  { code: "MOOV", nom: "Moov Money" },
];

const fcfa = (m) => `${m.toLocaleString("fr-FR")} CFA`;

/** Carte cadeau beige + formulaire de paiement Mobile Money (CinetPay). */
export const CadeauAnnonce = ({ annonceId, nom, photo }) => {
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [montant, setMontant] = useState(5000);
  const [libre, setLibre] = useState("");
  const [methode, setMethode] = useState("WAVE");
  const [form, setForm] = useState({ payer_name: "", payer_phone: "", message: "" });
  const [aSimuler, setASimuler] = useState(null);

  const maj = (cle) => (e) => setForm((f) => ({ ...f, [cle]: e.target.value }));
  const montantFinal = libre ? Number(libre) : montant;

  const envoyer = async (e) => {
    e.preventDefault();
    if (!montantFinal || montantFinal < MONTANT_MIN) {
      toast.error(`Montant minimum : ${fcfa(MONTANT_MIN)}`);
      return;
    }
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/cadeaux`, {
        annonce_id: annonceId, amount: montantFinal, method: methode, ...form,
      });
      if (data.payment_url) {
        window.location.href = data.payment_url;
        return;
      }
      setASimuler(data.cadeau_id);
      toast.info(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Paiement impossible pour le moment.");
    } finally {
      setEnvoi(false);
    }
  };

  const simuler = async () => {
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API}/cadeaux/${aSimuler}/simuler-paiement`);
      toast.success(data.message);
      setOuvert(false);
      setASimuler(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Simulation impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  const champ = "w-full rounded-xl border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#E05638]";

  return (
    <>
      <div className="rounded-2xl border border-[#E3D9C6] bg-[#F2E8D5] p-5" data-testid="cadeau-carte">
        <div className="flex items-center gap-3">
          {photo ? (
            <img src={photo} alt={nom} className="h-11 w-11 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="font-heading flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E05638]/15 font-extrabold text-[#E05638]">
              {(nom || "?").charAt(0)}
            </span>
          )}
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-500">
              Pour {nom}
            </p>
            <p className="font-heading text-xl font-extrabold text-stone-900">CFA 5 000</p>
          </div>
          <Gift size={20} className="ml-auto shrink-0 text-[#E05638]" />
        </div>
        <p className="mt-3 text-xs italic text-stone-500">Dépense, profite de la vie !</p>
        <h3 className="font-heading mt-3 text-base font-extrabold leading-tight text-stone-900">
          Envoie un peu d'amour à {nom}
        </h3>
        <p className="mt-1 text-xs text-stone-600">Illuminez la journée de {nom}.</p>
        <button
          type="button"
          onClick={() => setOuvert(true)}
          data-testid="cadeau-ouvrir"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E05638] py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#c74728]"
        >
          <Gift size={15} /> Envoyer un cadeau
        </button>
      </div>

      {ouvert && (
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/70 sm:items-center sm:p-4"
          data-testid="cadeau-modal">
          <form onSubmit={envoyer}
            className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl border border-white/10 bg-[#12100E] p-6 sm:max-w-md sm:rounded-3xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading text-lg font-extrabold text-white">Envoyer un cadeau</h3>
                <p className="mt-1 text-xs text-stone-400">Illuminez la journée de {nom}.</p>
              </div>
              <button type="button" onClick={() => { setOuvert(false); setASimuler(null); }}
                data-testid="cadeau-fermer"
                className="rounded-full p-1.5 text-stone-400 hover:bg-white/10 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2">
              {MONTANTS.map((m) => (
                <button key={m} type="button" onClick={() => { setMontant(m); setLibre(""); }}
                  data-testid={`cadeau-montant-${m}`}
                  className={`rounded-xl border py-2.5 text-sm font-bold transition-colors ${
                    !libre && montant === m
                      ? "border-[#E05638] bg-[#E05638]/15 text-[#E05638]"
                      : "border-white/15 text-stone-300 hover:border-white/40"}`}>
                  {fcfa(m)}
                </button>
              ))}
            </div>
            <input type="number" min={MONTANT_MIN} value={libre} onChange={(e) => setLibre(e.target.value)}
              placeholder={`Autre montant (min ${fcfa(MONTANT_MIN)})`} data-testid="cadeau-montant-libre"
              className={`${champ} mt-2`} />

            <div className="mt-4 grid grid-cols-2 gap-2">
              {METHODES.map((m) => (
                <button key={m.code} type="button" onClick={() => setMethode(m.code)}
                  data-testid={`cadeau-methode-${m.code}`}
                  className={`rounded-xl border py-2.5 text-xs font-bold transition-colors ${
                    methode === m.code
                      ? "border-[#E05638] bg-[#E05638]/15 text-[#E05638]"
                      : "border-white/15 text-stone-300 hover:border-white/40"}`}>
                  {m.nom}
                </button>
              ))}
            </div>

            <div className="mt-4 space-y-3">
              <input required value={form.payer_phone} onChange={maj("payer_phone")}
                placeholder="Votre numéro de paiement (+225...)" data-testid="cadeau-telephone" className={champ} />
              <input value={form.payer_name} onChange={maj("payer_name")}
                placeholder="Votre prénom (facultatif)" data-testid="cadeau-prenom" className={champ} />
              <textarea rows={2} maxLength={300} value={form.message} onChange={maj("message")}
                placeholder="Votre message (facultatif)" data-testid="cadeau-message"
                className={`${champ} resize-none`} />
            </div>

            {aSimuler ? (
              <button type="button" onClick={simuler} disabled={envoi} data-testid="cadeau-simuler"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#E05638] py-3 text-sm font-bold text-[#E05638] disabled:opacity-60">
                {envoi && <Loader2 size={15} className="animate-spin" />} Simuler le paiement
              </button>
            ) : (
              <button type="submit" disabled={envoi} data-testid="cadeau-payer"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E05638] py-3 text-sm font-bold text-white transition-colors hover:bg-[#c74728] disabled:opacity-60">
                {envoi ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />}
                Payer {fcfa(montantFinal || 0)}
              </button>
            )}
          </form>
        </div>
      )}
    </>
  );
};

export default CadeauAnnonce;
